import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { articles } from "@/mocks/gossip";
import { formatHeat, type GossipArticle } from "@/utils/gossip";

export const TOC_ITEMS = [
  { id: "overview", label: "事件概览" },
  { id: "timeline", label: "时间线梳理" },
  { id: "content", label: "正文整理" },
  { id: "sources", label: "信息来源" },
];

const HOT_TOP = [...articles].sort((a, b) => b.heat - a.heat).slice(0, 5);

interface ArticleSidebarProps {
  article: GossipArticle;
}

export default function ArticleSidebar({ article }: ArticleSidebarProps) {
  const [activeId, setActiveId] = useState(TOC_ITEMS[0].id);

  useEffect(() => {
    const sections = TOC_ITEMS.map((item) => document.getElementById(item.id)).filter(
      (el): el is HTMLElement => Boolean(el)
    );
    if (sections.length === 0) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: "-120px 0px -60% 0px", threshold: [0, 1] }
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [article.id]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <aside className="w-full lg:sticky lg:top-28 space-y-6" aria-label="文章辅助信息">
      <nav className="p-5 bg-background-50 border border-background-200 rounded-lg" aria-label="内容目录">
        <h2 className="font-heading text-sm font-semibold text-foreground-950 mb-3 inline-flex items-center gap-2">
          <i className="ri-list-check-2 text-primary-600"></i>
          内容目录
        </h2>
        <ul className="space-y-1">
          {TOC_ITEMS.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => scrollTo(item.id)}
                className={`w-full text-left px-3 py-2 text-sm font-label rounded-md transition-colors cursor-pointer ${
                  activeId === item.id
                    ? "bg-primary-100 text-primary-700"
                    : "text-foreground-600 hover:bg-background-100 hover:text-foreground-900"
                }`}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <div className="p-5 bg-background-100 border border-background-200 rounded-lg">
        <h2 className="font-heading text-sm font-semibold text-foreground-950 mb-3 inline-flex items-center gap-2">
          <i className="ri-fire-line text-primary-600"></i>
          今日热榜
        </h2>
        <ol className="space-y-3">
          {HOT_TOP.map((item, index) => (
            <li key={item.id} className="flex gap-3">
              <span
                className={`w-5 h-5 shrink-0 rounded flex items-center justify-center font-label text-[11px] font-bold mt-0.5 ${
                  index < 3 ? "bg-primary-500 text-background-50" : "bg-background-300 text-foreground-700"
                }`}
              >
                {index + 1}
              </span>
              <Link to={`/hot/${item.id}`} className="group min-w-0">
                <span className="block text-sm text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
                  {item.title}
                </span>
                <span className="mt-1 flex items-center gap-2 text-[11px] font-label text-foreground-500">
                  {item.categoryLabel}
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-fire-line text-primary-500"></i>
                    {formatHeat(item.heat)}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ol>
      </div>

      <div className="p-5 bg-secondary-950 border border-secondary-900 rounded-lg text-background-200">
        <h2 className="font-heading text-sm font-semibold text-background-50 mb-2">线索投稿</h2>
        <p className="text-xs leading-relaxed text-background-300/90 mb-4">
          手上有值得整理的瓜料线索？写明时间与公开来源，我们会先做基础核实。
        </p>
        <Link
          to="/contact"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-label font-medium rounded-md bg-accent-500 text-foreground-950 hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-send-plane-line"></i>
          提交线索
        </Link>
      </div>
    </aside>
  );
}